import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../App';
import { WeeklyComparison } from '../components/reports/WeeklyComparison';
import {
  Download, Search, Package, Truck, Store, ShoppingCart,
  ClipboardList, History, ChevronRight, Filter, TrendingUp, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const R = (v) => v != null ? `R${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'R0.00';

const Spinner = () => (
  <div className="flex items-center justify-center h-32">
    <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const ExportBtn = ({ onClick, label = "Export Excel" }) => (
  <Button variant="outline" size="sm" onClick={onClick} data-testid="export-excel-btn">
    <Download className="w-4 h-4 mr-1.5" />{label}
  </Button>
);

const TotalRow = ({ label, value, colSpan = 5 }) => (
  <TableRow className="bg-muted/50 font-semibold">
    <TableCell colSpan={colSpan} className="text-right">{label}</TableCell>
    <TableCell className="text-right">{R(value)}</TableCell>
  </TableRow>
);

// ═══════════════════════════════════════════════════
// INVENTORY VALUATION (based on confirmed stock takes)
// ═══════════════════════════════════════════════════
const InventoryValuation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    axios.get(`${API}/branches`, { withCredentials: true }).then(r => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    axios.get(`${API}/reports/inventory-valuation`, { withCredentials: true, params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load valuation'))
      .finally(() => setLoading(false));
  }, [filterBranch, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    const params = new URLSearchParams();
    if (filterBranch) params.set('branch_id', filterBranch);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${API}/reports/inventory-valuation/export?${params}`, '_blank');
  };

  const filtered = (data?.rows || []).filter(r =>
    (!search || r.item_name?.toLowerCase().includes(search.toLowerCase()) || r.supplier_name?.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredTotal = filtered.reduce((s, r) => s + (r.soh_value || 0), 0);

  return (
    <div className="space-y-4" data-testid="inventory-valuation-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
            <Select value={filterBranch || "all"} onValueChange={v => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name?.replace('The Merchant ', '')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
            <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Item or supplier..." className="pl-8 w-48" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">From</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="valuation-start-date" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">To</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="valuation-end-date" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{filtered.length} rows &middot; {data?.stock_take_count || 0} stock takes</span>
          <ExportBtn onClick={doExport} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">SOH</TableHead>
                <TableHead className="text-right">PAR</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">SOH Value</TableHead>
                <TableHead className="text-right">Order Qty</TableHead>
                <TableHead>Counted By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">No confirmed stock takes found. Complete a stock take to see valuations.</TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm whitespace-nowrap">{r.date?.slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{r.branch_name?.replace('The Merchant ', '')}</TableCell>
                      <TableCell className="text-sm font-medium">{r.item_name}</TableCell>
                      <TableCell className="text-sm">{r.supplier_name}</TableCell>
                      <TableCell className="text-sm">{r.unit}</TableCell>
                      <TableCell className="text-right text-sm">{r.soh}</TableCell>
                      <TableCell className="text-right text-sm">{r.par}</TableCell>
                      <TableCell className="text-right text-sm">{R(r.cost_price)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{R(r.soh_value)}</TableCell>
                      <TableCell className="text-right text-sm">{r.order_qty}</TableCell>
                      <TableCell className="text-sm">{r.counted_by}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-semibold border-t-2">
                    <TableCell colSpan={9} className="text-right">Total Inventory Value:</TableCell>
                    <TableCell className="text-right">{R(filteredTotal)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// STOCK TAKES REPORT
// ═══════════════════════════════════════════════════
const StockTakesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    axios.get(`${API}/branches`, { withCredentials: true }).then(r => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    if (confirmedOnly) params.confirmed_only = true;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    axios.get(`${API}/reports/stock-takes`, { withCredentials: true, params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [filterBranch, confirmedOnly, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    const params = new URLSearchParams();
    if (filterBranch) params.set('branch_id', filterBranch);
    if (confirmedOnly) params.set('confirmed_only', 'true');
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${API}/reports/stock-takes/export?${params}`, '_blank');
  };

  const filtered = (data?.rows || []).filter(r =>
    (!search || r.item_name?.toLowerCase().includes(search.toLowerCase()) || r.supplier_name?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="stock-takes-report-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
            <Select value={filterBranch || "all"} onValueChange={v => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name?.replace('The Merchant ', '')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant={confirmedOnly ? "default" : "outline"} size="sm" onClick={() => setConfirmedOnly(!confirmedOnly)}>
              {confirmedOnly ? "Confirmed Only" : "All Status"}
            </Button>
          </div>
          <div className="relative">
            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
            <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Item or supplier..." className="pl-8 w-48" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">From</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="st-start-date" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">To</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="st-end-date" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{filtered.length} rows</span>
          <ExportBtn onClick={doExport} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">SOH</TableHead>
                <TableHead className="text-right">PAR</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">SOH Value</TableHead>
                <TableHead className="text-right">Order Qty</TableHead>
                <TableHead className="text-right">Order Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground">No stock takes found</TableCell></TableRow>
              ) : filtered.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm whitespace-nowrap">{r.date?.slice(0, 10)}</TableCell>
                  <TableCell className="text-sm">{r.branch_name?.replace('The Merchant ', '')}</TableCell>
                  <TableCell><Badge variant={r.confirmed ? "default" : "outline"} className="text-xs">{r.confirmed ? "Confirmed" : "Pending"}</Badge></TableCell>
                  <TableCell className="text-sm font-medium">{r.item_name}</TableCell>
                  <TableCell className="text-sm">{r.supplier_name}</TableCell>
                  <TableCell className="text-sm">{r.unit}</TableCell>
                  <TableCell className="text-right text-sm">{r.soh}</TableCell>
                  <TableCell className="text-right text-sm">{r.par}</TableCell>
                  <TableCell className={`text-right text-sm ${r.variance < 0 ? 'text-red-600' : r.variance > 0 ? 'text-green-600' : ''}`}>{r.variance}</TableCell>
                  <TableCell className="text-right text-sm">{R(r.cost_price)}</TableCell>
                  <TableCell className="text-right text-sm">{R(r.soh_value)}</TableCell>
                  <TableCell className="text-right text-sm">{r.order_qty}</TableCell>
                  <TableCell className="text-right text-sm">{R(r.order_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// ORDERS REPORT
// ═══════════════════════════════════════════════════
const OrdersReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/branches`, { withCredentials: true }),
      axios.get(`${API}/suppliers`, { withCredentials: true })
    ]).then(([b, s]) => { setBranches(b.data); setSuppliers(s.data); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    if (filterSupplier) params.supplier_id = filterSupplier;
    if (filterStatus) params.status = filterStatus;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    axios.get(`${API}/reports/orders`, { withCredentials: true, params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [filterBranch, filterSupplier, filterStatus, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    const params = new URLSearchParams();
    if (filterBranch) params.set('branch_id', filterBranch);
    if (filterSupplier) params.set('supplier_id', filterSupplier);
    if (filterStatus) params.set('status', filterStatus);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${API}/reports/orders/export?${params}`, '_blank');
  };

  const filtered = (data?.rows || []).filter(r =>
    (!search || r.item_name?.toLowerCase().includes(search.toLowerCase()) || r.supplier_name?.toLowerCase().includes(search.toLowerCase()))
  );
  const filteredTotal = filtered.reduce((s, r) => s + (r.line_value || 0), 0);

  return (
    <div className="space-y-4" data-testid="orders-report-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
            <Select value={filterBranch || "all"} onValueChange={v => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name?.replace('The Merchant ', '')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Supplier</label>
            <Select value={filterSupplier || "all"} onValueChange={v => setFilterSupplier(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Status</label>
            <Select value={filterStatus || "all"} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="printed">Printed</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="received">Received</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
            <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Item or supplier..." className="pl-8 w-48" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">From</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="orders-start-date" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">To</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="orders-end-date" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{data?.total_orders || 0} orders &middot; {filtered.length} lines</span>
          <ExportBtn onClick={doExport} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">SOH</TableHead>
                <TableHead className="text-right">PAR</TableHead>
                <TableHead className="text-right">Order Qty</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Line Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">No orders found</TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm whitespace-nowrap">{r.date?.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs font-mono">{r.order_id?.slice(-8)}</TableCell>
                      <TableCell className="text-sm">{r.branch_name?.replace('The Merchant ', '')}</TableCell>
                      <TableCell className="text-sm">{r.supplier_name}</TableCell>
                      <TableCell><Badge variant={r.status === 'received' ? 'default' : 'outline'} className="text-xs">{r.status}</Badge></TableCell>
                      <TableCell className="text-sm font-medium">{r.item_name}</TableCell>
                      <TableCell className="text-sm">{r.unit}</TableCell>
                      <TableCell className="text-right text-sm">{r.soh}</TableCell>
                      <TableCell className="text-right text-sm">{r.par}</TableCell>
                      <TableCell className="text-right text-sm">{r.order_qty}</TableCell>
                      <TableCell className="text-right text-sm">{R(r.cost_price)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{R(r.line_value)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-semibold border-t-2">
                    <TableCell colSpan={11} className="text-right">Total:</TableCell>
                    <TableCell className="text-right">{R(filteredTotal)}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// SUPPLIER ANALYSIS
// ═══════════════════════════════════════════════════
const SupplierAnalysis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/reports/supplier-analysis`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const doExport = () => window.open(`${API}/reports/supplier-analysis/export`, '_blank');

  const filtered = data.filter(s => !search || s.supplier_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="supplier-analysis-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="relative">
          <label className="text-xs text-muted-foreground mb-1 block">Search</label>
          <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Supplier name..." className="pl-8 w-64" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{filtered.length} suppliers</span>
          <ExportBtn onClick={doExport} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead className="text-right">PAR Stock Value</TableHead>
                <TableHead className="text-right">POs</TableHead>
                <TableHead className="text-right">Order Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(s => (
                <TableRow key={s.supplier_id}>
                  <TableCell className="font-medium">{s.supplier_name}</TableCell>
                  <TableCell className="text-sm">{s.contact_name || '-'}</TableCell>
                  <TableCell className="text-sm">{s.phone || '-'}</TableCell>
                  <TableCell className="text-sm">{s.email || '-'}</TableCell>
                  <TableCell className="text-right">{s.item_count}</TableCell>
                  <TableCell className="text-right font-medium">{R(s.total_par_value)}</TableCell>
                  <TableCell className="text-right">{s.order_count}</TableCell>
                  <TableCell className="text-right">{R(s.total_order_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// ITEM ANALYSIS (with drill-down)
// ═══════════════════════════════════════════════════
const ItemAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetail, setItemDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/branches`, { withCredentials: true }),
      axios.get(`${API}/suppliers`, { withCredentials: true })
    ]).then(([b, s]) => { setBranches(b.data); setSuppliers(s.data); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    if (filterSupplier) params.supplier_id = filterSupplier;
    axios.get(`${API}/reports/item-analysis`, { withCredentials: true, params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [filterBranch, filterSupplier]);

  useEffect(() => { load(); }, [load]);

  const doExport = () => {
    const params = new URLSearchParams();
    if (filterBranch) params.set('branch_id', filterBranch);
    if (filterSupplier) params.set('supplier_id', filterSupplier);
    window.open(`${API}/reports/item-analysis/export?${params}`, '_blank');
  };

  const openDetail = (itemId) => {
    setSelectedItem(itemId);
    setDetailLoading(true);
    axios.get(`${API}/reports/item-detail/${itemId}`, { withCredentials: true })
      .then(r => setItemDetail(r.data))
      .catch(() => toast.error('Failed to load item detail'))
      .finally(() => setDetailLoading(false));
  };

  const branchCols = data?.branches || [];
  const filtered = (data?.rows || []).filter(r =>
    (!search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.sku?.toLowerCase().includes(search.toLowerCase()) || r.supplier_name?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="item-analysis-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
            <Select value={filterBranch || "all"} onValueChange={v => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name?.replace('The Merchant ', '')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Supplier</label>
            <Select value={filterSupplier || "all"} onValueChange={v => setFilterSupplier(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="text-xs text-muted-foreground mb-1 block">Search</label>
            <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="SKU, name, supplier..." className="pl-8 w-56" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{filtered.length} items</span>
          <ExportBtn onClick={doExport} />
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                {branchCols.map(b => (
                  <TableHead key={b.id} className="text-right">{b.short_name} PAR</TableHead>
                ))}
                <TableHead className="text-right">Total PAR Value</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7 + branchCols.length + 1} className="text-center py-8 text-muted-foreground">No items found</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.stock_item_id} className={selectedItem === r.stock_item_id ? 'bg-primary/5' : ''}>
                  <TableCell className="text-xs font-mono">{r.sku || '-'}</TableCell>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm">{r.supplier_name}</TableCell>
                  <TableCell className="text-sm">{r.unit}</TableCell>
                  <TableCell className="text-right text-sm">{R(r.cost_price)}</TableCell>
                  {branchCols.map(b => (
                    <TableCell key={b.id} className="text-right text-sm">
                      {r[`par_${b.short_name}`] || 0}
                      {r[`avail_${b.short_name}`] === 'N' && <span className="text-muted-foreground/50 ml-0.5 text-xs">*</span>}
                    </TableCell>
                  ))}
                  <TableCell className="text-right text-sm font-medium">{R(r.total_par_value)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => openDetail(r.stock_item_id)} data-testid={`item-detail-btn-${r.stock_item_id}`}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Item Detail Drill-down */}
      {selectedItem && (
        <div className="border rounded-md p-4 space-y-4 bg-muted/30" data-testid="item-detail-drilldown">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{itemDetail?.item?.name || 'Loading...'}</h3>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedItem(null); setItemDetail(null); }}>Close</Button>
          </div>

          {detailLoading ? <Spinner /> : itemDetail && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Branch PAR */}
              <div>
                <h4 className="text-sm font-medium mb-2">Branch PAR Values</h4>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Branch</TableHead><TableHead className="text-right">PAR</TableHead><TableHead className="text-right">PAR Value</TableHead><TableHead>Stocked</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {itemDetail.branches?.map((b, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{b.branch_name?.replace('The Merchant ', '')}</TableCell>
                        <TableCell className="text-right text-sm">{b.par_value}</TableCell>
                        <TableCell className="text-right text-sm">{R(b.par_cost)}</TableCell>
                        <TableCell><Badge variant={b.available ? "default" : "outline"} className="text-xs">{b.available ? "Y" : "N"}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Price History */}
              <div>
                <h4 className="text-sm font-medium mb-2">Price History</h4>
                {itemDetail.price_history?.length > 0 ? (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead className="text-right">Old</TableHead><TableHead className="text-right">New</TableHead><TableHead className="text-right">Change</TableHead><TableHead>By</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {itemDetail.price_history.map((ph, i) => {
                        const pct = ph.old_price > 0 ? (((ph.new_price - ph.old_price) / ph.old_price) * 100) : 0;
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{new Date(ph.changed_at).toLocaleDateString('en-ZA')}</TableCell>
                            <TableCell className="text-right text-sm">{R(ph.old_price)}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{R(ph.new_price)}</TableCell>
                            <TableCell className={`text-right text-sm ${pct > 0 ? 'text-red-600' : 'text-green-600'}`}>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</TableCell>
                            <TableCell className="text-sm">{ph.changed_by}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground">No price changes recorded</p>}
              </div>

              {/* Stock Take History */}
              {itemDetail.stock_take_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Stock Take History</h4>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead>Branch</TableHead><TableHead className="text-right">SOH</TableHead><TableHead className="text-right">PAR</TableHead><TableHead className="text-right">Order</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {itemDetail.stock_take_history.map((st, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{st.date?.slice(0, 10)}</TableCell>
                          <TableCell className="text-sm">{st.branch_name?.replace('The Merchant ', '')}</TableCell>
                          <TableCell className="text-right text-sm">{st.soh}</TableCell>
                          <TableCell className="text-right text-sm">{st.par}</TableCell>
                          <TableCell className="text-right text-sm">{st.order_qty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Recipe Usage */}
              {itemDetail.recipes_using?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Used in {itemDetail.recipes_using.length} Recipes</h4>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Recipe</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Cost</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {itemDetail.recipes_using.map(r => (
                        <TableRow key={r.recipe_id}>
                          <TableCell className="text-sm font-medium">{r.name}</TableCell>
                          <TableCell className="text-sm">{r.category}</TableCell>
                          <TableCell className="text-right text-sm">{R(r.total_cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// VALUATION TRENDS
// ═══════════════════════════════════════════════════
const ValuationTrends = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    axios.get(`${API}/branches`, { withCredentials: true }).then(r => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterBranch) params.branch_id = filterBranch;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    axios.get(`${API}/reports/valuation-trends`, { withCredentials: true, params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load trends'))
      .finally(() => setLoading(false));
  }, [filterBranch, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const trends = data?.trends || [];
  const maxVal = Math.max(...trends.map(t => t.total), 1);

  return (
    <div className="space-y-4" data-testid="valuation-trends-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
            <Select value={filterBranch || "all"} onValueChange={v => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name?.replace('The Merchant ', '')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">From</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="trends-start-date" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">To</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="trends-end-date" />
          </div>
        </div>
        <span className="text-sm text-muted-foreground">{trends.length} data points</span>
      </div>

      {loading ? <Spinner /> : trends.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">No valuation data yet</p>
          <p className="text-sm">Complete stock takes to see inventory valuation over time</p>
        </div>
      ) : (
        <>
          {/* Bar chart visualization */}
          <div className="border rounded-lg p-6 bg-card">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Total Inventory Value Over Time</h3>
            <div className="flex items-end gap-1 h-64 overflow-x-auto pb-2">
              {trends.map((t, i) => {
                const pct = (t.total / maxVal) * 100;
                return (
                  <div key={i} className="flex flex-col items-center gap-1 min-w-[48px] flex-1" title={`${t.date}: ${R(t.total)}`}>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{R(t.total)}</span>
                    <div
                      className="w-full bg-primary/80 rounded-t-sm hover:bg-primary transition-colors cursor-default"
                      style={{ height: `${Math.max(pct, 2)}%`, minHeight: '4px' }}
                    />
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{t.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Branch breakdown table */}
          <div className="border rounded-md overflow-auto max-h-[400px]">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                  {Object.keys(trends[0]?.branches || {}).map(b => (
                    <TableHead key={b} className="text-right">{b.replace('The Merchant ', '')}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {trends.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{t.date}</TableCell>
                    <TableCell className="text-right text-sm font-semibold">{R(t.total)}</TableCell>
                    {Object.entries(t.branches).map(([b, v]) => (
                      <TableCell key={b} className="text-right text-sm">{R(v)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// MAIN REPORTS PAGE
// ═══════════════════════════════════════════════════
const Reports = () => {
  const { user } = useAuth();
  const canSeeWeekly = true;
  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Reports
        </h1>
        <p className="text-muted-foreground mt-1">Detailed breakdowns with Excel export. Valuations are based on confirmed stock take SOH values.</p>
      </div>

      <Tabs defaultValue="valuation" className="space-y-4">
        <TabsList>
          {canSeeWeekly && <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly Comparison</TabsTrigger>}
          <TabsTrigger value="valuation" data-testid="tab-valuation">Inventory Valuation</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends">Valuation Trends</TabsTrigger>
          <TabsTrigger value="stocktakes" data-testid="tab-stocktakes">Stock Takes</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>
        </TabsList>

        {canSeeWeekly && <TabsContent value="weekly"><WeeklyComparison /></TabsContent>}
        <TabsContent value="valuation"><InventoryValuation /></TabsContent>
        <TabsContent value="trends"><ValuationTrends /></TabsContent>
        <TabsContent value="stocktakes"><StockTakesReport /></TabsContent>
        <TabsContent value="orders"><OrdersReport /></TabsContent>
        <TabsContent value="suppliers"><SupplierAnalysis /></TabsContent>
        <TabsContent value="items"><ItemAnalysis /></TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
